import { useEffect, useState } from 'react';
import { Modal, Button, Input } from '../primitives';
import { MapPickerButton } from '../map';
import { useCreateStavba, useUpdateStavba } from '../../api/hooks';
import { useActiveStavba } from '../../features/stavba-context/StavbaContext';
import type { Stavba } from '../../api/schemas';
import styles from './NewStavbaModal.module.css';

/**
 * Modal pro založení nové stavby (/api/stavby/ POST) i pro dodatečnou úpravu
 * základních informací o existující stavbě (PUT /api/stavby/{id}).
 *
 * Když je předána `stavba`, modal přepne do režimu úpravy: předvyplní pole,
 * změní titulek/akci a uloží přes update. Bez `stavba` se chová jako dřív —
 * po úspěšném založení stavbu rovnou nastaví jako aktivní (globální kontext)
 * a zavolá `onCreated`.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (stavba: Stavba) => void;
  /** Když je vyplněno, modal je v režimu úpravy této stavby. */
  stavba?: Stavba | null;
}

/** ISO datum z <input type="date"> → český formát "d. m. yyyy" (model = String(16)). */
function isoToCz(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)}. ${Number(m)}. ${y}`;
}

/** Český formát "d. m. yyyy" (nebo ISO) → ISO "yyyy-mm-dd" pro <input type="date">. */
function czToIso(value: string): string {
  if (!value) return '';
  const czech = value.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (czech) {
    const [, d, m, y] = czech;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Už ISO? Vezmeme jen datovou část.
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : '';
}

export function NewStavbaModal({ open, onClose, onCreated, stavba }: Props) {
  const create = useCreateStavba();
  const update = useUpdateStavba();
  const { setActiveStavbaId } = useActiveStavba();

  const isEdit = !!stavba;
  const busy = isEdit ? update.isPending : create.isPending;

  const [nazev, setNazev] = useState('');
  const [zakazka, setZakazka] = useState('');
  const [adresa, setAdresa] = useState('');
  const [povoleni, setPovoleni] = useState('');
  const [zahajeni, setZahajeni] = useState('');
  const [ukonceni, setUkonceni] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Při otevření předvyplníme pole (úprava) nebo vyčistíme (nová stavba).
  useEffect(() => {
    if (!open) return;
    setNazev(stavba?.nazev ?? '');
    setZakazka(stavba?.zakazka_cislo ?? '');
    setAdresa(stavba?.adresa ?? '');
    setPovoleni(stavba?.stavebni_povoleni ?? '');
    setZahajeni(czToIso(stavba?.datum_zahajeni ?? ''));
    setUkonceni(czToIso(stavba?.datum_ukonceni ?? ''));
    setFormError(null);
  }, [open, stavba]);

  function reset() {
    setNazev('');
    setZakazka('');
    setAdresa('');
    setPovoleni('');
    setZahajeni('');
    setUkonceni('');
    setFormError(null);
  }

  function resetAndClose() {
    reset();
    onClose();
  }

  async function handleSave() {
    if (!nazev.trim()) {
      setFormError('Název stavby je povinný.');
      return;
    }
    setFormError(null);
    const payload = {
      nazev: nazev.trim(),
      adresa: adresa.trim(),
      zakazka_cislo: zakazka.trim(),
      stavebni_povoleni: povoleni.trim(),
      datum_zahajeni: isoToCz(zahajeni),
      datum_ukonceni: isoToCz(ukonceni),
    };
    try {
      if (isEdit && stavba) {
        await update.mutateAsync({ id: stavba.id, data: payload });
        resetAndClose();
        return;
      }
      const created = await create.mutateAsync(payload);
      setActiveStavbaId(created.id);
      reset();
      onCreated?.(created);
      onClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Nepodařilo se uložit stavbu.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      title={isEdit ? 'Upravit stavbu' : 'Nová stavba'}
      footer={
        <>
          <Button variant="ghost" onClick={resetAndClose}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={busy}>
            {busy
              ? isEdit
                ? 'Ukládám…'
                : 'Zakládám…'
              : isEdit
                ? 'Uložit změny'
                : 'Založit stavbu'}
          </Button>
        </>
      }
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <Input
          label="Název stavby"
          placeholder="Např. Rekonstrukce ZŠ Komenského"
          required
          value={nazev}
          onChange={(e) => setNazev(e.target.value)}
          autoFocus
        />

        <div className={styles.grid2}>
          <Input
            label="Č. zakázky"
            mono
            compact
            value={zakazka}
            onChange={(e) => setZakazka(e.target.value)}
          />
          <Input
            label="Stavební povolení"
            compact
            value={povoleni}
            onChange={(e) => setPovoleni(e.target.value)}
          />
        </div>

        <div className={styles.addressRow}>
          <Input
            label="Adresa"
            compact
            value={adresa}
            onChange={(e) => setAdresa(e.target.value)}
          />
          <MapPickerButton value={adresa} onApply={setAdresa} title="Adresa stavby" label="Mapa" />
        </div>

        <div className={styles.grid2}>
          <Input
            label="Datum zahájení"
            type="date"
            compact
            value={zahajeni}
            onChange={(e) => setZahajeni(e.target.value)}
          />
          <Input
            label="Datum dokončení"
            type="date"
            compact
            value={ukonceni}
            onChange={(e) => setUkonceni(e.target.value)}
          />
        </div>

        {formError && <div className={styles.error}>{formError}</div>}
      </form>
    </Modal>
  );
}
